import { inject, Injectable, signal } from '@angular/core';
import { areIntervalsOverlapping, format, max, min } from 'date-fns';
import { catchError, Observable, of, tap, throwError } from 'rxjs';

import { isHttpErrorResponse } from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../../components/dialog/dialog.service';
import {
  ClientEventConflictResponseModel,
  ClientEventResponseModel,
} from '../../../models/clients.model';
import {
  ClientEventCreateModel,
  ClientEventFromTemplateModel,
  ClientEventUpdateModel,
  CopyDayRequest,
  CopyEventsResponse,
  CopyWeekRequest,
  ValidationResult,
} from '../models/event-operations.model';
import { CalendarManagerService } from './calendar-manager.service';
import { ClientEventService } from './client-event.service';

@Injectable({
  providedIn: 'root',
})
export class EventOperationsService {
  private readonly _clientEventService = inject(ClientEventService);
  private readonly _calendarManager = inject(CalendarManagerService);
  private readonly _dialog = inject(DialogService);

  readonly eventConflicts = signal<ClientEventConflictResponseModel[]>([]);

  /**
   * The backend error code carried by a failed request, or null.
   *
   * Only errors the backend raises deliberately carry the
   * `{ detail: { errorCode } }` envelope. An unhandled 500 comes back as
   * Starlette's plain-text "Internal Server Error", so `error` is not that
   * envelope and `detail` is undefined -- reading `.errorCode` off it throws
   * a TypeError inside the `catchError`, which loses the original response
   * and skips the dialog the user should have seen. That is how a failing
   * "plan from template" showed nothing at all.
   */
  private _errorCode(e: unknown): string | null {
    if (!isHttpErrorResponse(e)) {
      return null;
    }

    const body = e.error as { detail?: { errorCode?: string } } | null | undefined;

    return body?.detail?.errorCode ?? null;
  }

  // CRUD Operations
  createEvent(clientId: string, data: ClientEventCreateModel): Observable<string> {
    return this._clientEventService.create(clientId, data).pipe(
      catchError((e) => {
        const dialog = {
          title: 'Foutmelding',
          message:
            'Er is iets misgegaan bij het aanmaken van een event. Probeer het later nog eens',
          confirmText: 'OK',
        };

        if (isHttpErrorResponse(e)) {
          if (this._errorCode(e) === 'CLIENT_EVENT_OVERLAP') {
            dialog.message =
              'Het event kan niet worden aangemaakt. Event overlapt met bestaande events';
          } else if (this._errorCode(e) === 'CLIENT_EVENT_IN_PAST') {
            dialog.message =
              'Het event kan niet worden aangemaakt omdat de starttijd in het verleden ligt';
          }
        }

        this._dialog.alert(dialog);

        return throwError(() => e);
      })
    );
  }

  createEventFromTemplate(
    clientId: string,
    data: ClientEventFromTemplateModel
  ): Observable<string> {
    return this._clientEventService.createFromTemplate(clientId, data).pipe(
      catchError((e) => {
        const dialog = {
          title: 'Foutmelding',
          message:
            'Er is iets misgegaan bij het aanmaken van een event vanuit een template. Probeer het later nog eens',
          confirmText: 'OK',
        };

        if (isHttpErrorResponse(e)) {
          if (this._errorCode(e) === 'CLIENT_EVENT_OVERLAP') {
            dialog.message =
              'Het event kan niet worden aangemaakt. Event overlapt met bestaande events';
          } else if (this._errorCode(e) === 'CLIENT_EVENT_IN_PAST') {
            dialog.message =
              'Het event kan niet worden aangemaakt omdat de starttijd in het verleden ligt';
          } else if (this._errorCode(e) === 'UNABLE_TO_ATTACH_TEMPLATE') {
            dialog.message = 'De template is nog niet gepubliceerd';
          }
        }

        this._dialog.alert(dialog);

        return throwError(() => e);
      })
    );
  }

  createMediaEvent(clientId: string, data: unknown): Observable<string> {
    return this._clientEventService.createMediaEvent(clientId, data).pipe(
      catchError((e) => {
        const dialog = {
          title: 'Foutmelding',
          message:
            'Er is iets misgegaan bij het aanmaken van een media event. Probeer het later nog eens',
          confirmText: 'OK',
        };

        if (isHttpErrorResponse(e)) {
          if (this._errorCode(e) === 'CLIENT_EVENT_OVERLAP') {
            dialog.message =
              'Het event kan niet worden aangemaakt. Event overlapt met bestaande events';
          } else if (this._errorCode(e) === 'CLIENT_EVENT_IN_PAST') {
            dialog.message =
              'Het event kan niet worden aangemaakt omdat de starttijd in het verleden ligt';
          }
        }

        this._dialog.alert(dialog);

        return throwError(() => e);
      })
    );
  }

  updateEvent(
    clientId: string,
    eventId: string,
    data: ClientEventUpdateModel
  ): Observable<unknown> {
    return this._clientEventService.update(clientId, eventId, data).pipe(
      catchError((e) => {
        const dialog = {
          title: 'Foutmelding',
          message:
            'Er is iets misgegaan bij het bewerken van het event. Probeer het later nog eens',
          confirmText: 'OK',
        };

        if (isHttpErrorResponse(e)) {
          if (this._errorCode(e) === 'CLIENT_EVENT_OVERLAP') {
            dialog.message =
              'Het event kan niet worden bewerkt. Event overlapt met bestaande events';
          }
        }

        this._dialog.alert(dialog);

        return throwError(() => e);
      })
    );
  }

  deleteEvent(clientId: string, eventId: string): Observable<unknown> {
    return this._clientEventService.delete(clientId, eventId).pipe(
      tap(() => {
        this._calendarManager.removeEvent(eventId);
      })
    );
  }

  // Conflict Management
  getConflicts(clientId: string): Observable<ClientEventConflictResponseModel[]> {
    return this._clientEventService.getConflicts(clientId).pipe(
      tap((result) => this.eventConflicts.set(result)),
      catchError(() => of<ClientEventConflictResponseModel[]>([]))
    );
  }

  // Validation Methods
  validateEventInterval(start: Date, end: Date): ValidationResult {
    if (this.isInPast(start)) {
      return {
        valid: false,
        error: 'Event kan niet in het verleden worden geplaatst',
      };
    }

    if (this.doesIntervalOverlap({ start, end })) {
      return {
        valid: false,
        error: 'Event overlapt met bestaande events',
      };
    }

    return { valid: true };
  }

  doesIntervalOverlap(interval: { start: Date; end: Date }): boolean {
    const events = this._calendarManager.getEvents();

    return events.some(
      (event) =>
        event.start &&
        event.end &&
        areIntervalsOverlapping(interval, {
          start: event.start,
          end: event.end,
        })
    );
  }

  isInPast(start: Date): boolean {
    return start < new Date();
  }

  getIntervalOverlap(
    intervalLeft: { start: Date; end: Date },
    intervalRight: { start: Date; end: Date }
  ): { start: Date; end: Date } | null {
    if (!areIntervalsOverlapping(intervalLeft, intervalRight, { inclusive: true })) {
      return null;
    }

    const overlapStart = max([intervalLeft.start, intervalRight.start]);
    const overlapEnd = min([intervalLeft.end, intervalRight.end]);

    return {
      start: overlapStart,
      end: overlapEnd,
    };
  }

  // Fetch events by range
  fetchEventsByRange(
    clientId: string,
    range: { start: Date; end: Date }
  ): Observable<ClientEventResponseModel[]> {
    return this._clientEventService.getByRange(clientId, range);
  }

  copyDayEvents(
    clientId: string,
    sourceDate: Date,
    targetDate: Date,
  ): Observable<CopyEventsResponse> {
    return this._clientEventService
      .copyDay(clientId, {
        sourceDate: format(sourceDate, 'yyyy-MM-dd'),
        targetDate: format(targetDate, 'yyyy-MM-dd'),
      })
      .pipe(
        catchError((e) => {
          const message =
            isHttpErrorResponse(e) &&
            this._errorCode(e) === 'CLIENT_EVENT_IN_PAST'
              ? 'De doeldag ligt in het verleden'
              : 'Er is iets misgegaan bij het kopiëren van de events';

          this._dialog.alert({ title: 'Foutmelding', message, confirmText: 'OK' });

          return throwError(() => e);
        }),
      );
  }

  copyWeekEvents(
    clientId: string,
    sourceWeekStart: Date,
    targetWeekStart: Date,
  ): Observable<CopyEventsResponse> {
    return this._clientEventService
      .copyWeek(clientId, {
        sourceWeekStart: format(sourceWeekStart, 'yyyy-MM-dd'),
        targetWeekStart: format(targetWeekStart, 'yyyy-MM-dd'),
      })
      .pipe(
        catchError((e) => {
          const message =
            isHttpErrorResponse(e) &&
            this._errorCode(e) === 'CLIENT_EVENT_IN_PAST'
              ? 'De doelweek ligt in het verleden'
              : 'Er is iets misgegaan bij het kopiëren van de events';

          this._dialog.alert({ title: 'Foutmelding', message, confirmText: 'OK' });

          return throwError(() => e);
        }),
      );
  }
}
